import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deleteCommunityApi } from '../../../api/communities';
import { fetchCommunities } from '../../../store/slices/communitySlice';
import { Button, Modal } from 'react-bootstrap';
import DummyAvatar from '../../../assets/images/avatar.png';

export const CommunityCard = ({ community }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { filters } = useSelector((state) => state.community);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const ownerId = typeof community.owner === 'object' ? community.owner?._id : community.owner;
  const isOwner = user && ownerId === user._id;

  const getButtonText = () => {
    if (isOwner) return 'Manage';
    
    const type = community.type || 'free';
    const status = community.requestStatus || 'not_requested';
    const isPaid = community.isPaidMember;

    if (status === 'approved') {
      if (type === 'free' || isPaid) {
        return 'Joined';
      }
      if (type === 'freetrial' && !isPaid) {
        return 'Pay & Join';
      }
      if (type === 'monthly' && !isPaid) {
        return 'Subscribe';
      }
      if (type === 'onetime' && !isPaid) {
        return 'Pay & Join';
      }
    }
    
    if (status === 'pending') {
      return 'Request Sent';
    }

    if (type === 'free') {
      return 'Join Free';
    }
    if (type === 'freetrial') {
      return 'Start Trial';
    }
    if (type === 'monthly') {
      return 'Subscribe';
    }
    if (type === 'onetime') {
      return 'Pay & Join';
    }
    
    return 'Join Community';
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    navigate('/edit-community', { state: { id: community._id } });
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteCommunityApi(community._id);
      const params = {
        search: filters.search,
        type: filters.type,
        category: filters.selectedCategory,
      };
      dispatch(fetchCommunities(params));
      setShowDeleteModal(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete community');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClick = () => {
    navigate('/community-detail', { state: { id: community._id } });
  };

  return (
    <>
      <div className="community-card card-hover bg-white border h-100 rounded-4 overflow-hidden pointer" onClick={handleClick}>
        <div className="position-relative">
          <img
            src={community.coverImage || 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=500&h=280&fit=crop'}
            alt={community.title || community.name}
            className="img-fluid w-100 object-fit-cover"
            style={{ height: "180px" }}
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=500&h=280&fit=crop'; }}
          />
          {community.isLive && (
            <span className="badge-live d-flex align-items-center justify-content-center gap-1 position-absolute top-0 end-0 m-2 bg-danger text-xs text-white fw-bold px-3 py-1 rounded-pill shadow-sm z-3">
              <span className="live-dot bg-white d-inline-block rounded-circle" style={{ width: '6px', height: '6px' }}></span>
              LIVE
            </span>
          )}
          {isOwner && (
            <div className="card-actions position-absolute top-0 start-0 m-2 d-flex gap-2" style={{ zIndex: 4 }}>
              <Button
                variant="light"
                size="sm"
                className="rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                style={{ width: '28px', height: '28px', padding: 0 }}
                onClick={handleEdit}
                title="Edit Community"
              >
                <i className="bi bi-pencil-square text-primary" style={{ fontSize: '0.85rem' }}></i>
              </Button>
              <Button
                variant="light"
                size="sm"
                className="rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                style={{ width: '28px', height: '28px', padding: 0 }}
                onClick={(e) => { e.stopPropagation(); setShowDeleteModal(true); }}
                title="Delete Community"
              >
                <i className="bi bi-trash text-danger" style={{ fontSize: '0.85rem' }}></i>
              </Button>
            </div>
          )}
        </div>
        <div className="item-info p-3">
          {/* Avatar and name */}
          <div className="card-owner-row d-flex align-items-center gap-2 mb-2">
            <div className="owner-avatar rounded-circle overflow-hidden border border-light-subtle shadow-sm" style={{ width: '28px', height: '28px', flexShrink: 0 }}>
              <img
                src={community.owner?.profile_picture || community.owner?.avatar || DummyAvatar}
                alt={community.owner?.full_name || 'Owner'}
                className="w-100 h-100 object-fit-cover"
                onError={(e) => { e.target.src = DummyAvatar; }}
              />
            </div>
            <div className="community-card-title fw-bold text-gray-900 text-truncate m-0">
              {community.title || community.name}
            </div>
          </div>

          {/* Details row: Category, Students, Price */}
          <div className="card-details-row d-flex align-items-center flex-wrap row-gap-1 column-gap-3 text-xs fw-medium text-gray-800 mb-3">
            <span className="detail-item d-flex align-items-center gap-1">
              <i className="bi bi-folder-fill"></i>
              <span className="category-text">
                {(community?.category && typeof community.category === 'object')
                  ? community.category.name
                  : (community?.categoryName || community?.category || 'General')}
              </span>
            </span>
            <span className="detail-item d-flex align-items-center gap-1">
              <i className="bi bi-people-fill"></i>
              <span>{community.membersCount || 0} Students</span>
            </span>
            <span className="detail-item d-flex align-items-center gap-1">
              <i className="bi bi-tag-fill"></i>
              <span>
                {community?.subscriptionFee > 0 ? `$${community.subscriptionFee}/m` : 'Free Community'}
              </span>
            </span>
          </div>

          {/* Bottom row: Description (left) and Join Button (right) */}
          <div className="card-footer-action d-flex align-items-center justify-content-between gap-2">
            <p className="card-desc text-sm text-muted text-truncate-2 mb-0 flex-grow-1">
              {community.description || 'Join our interactive learning community to connect, share resources, and grow your skills together with like-minded students.'}
            </p>
            <Button size='sm'
              variant={community.requestStatus === 'pending' ? 'outline-secondary' : (community.requestStatus === 'approved' && (community.type === 'free' || community.isPaidMember) ? 'outline-primary' : 'primary')}
              className="btn-join-community text-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
              disabled={community.requestStatus === 'pending'}
            >
              {getButtonText()}
            </Button>
          </div>
        </div>
      </div>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} onClick={(e) => e.stopPropagation()} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Community</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the community <strong>{community?.name}</strong>? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

